<!-- SetPassword.vue -->
<template>
	<div class="min-h-screen bg-gray-50 flex">
		<!-- Left Panel - Brand Banner -->
		<div class="hidden lg:flex relative flex-1">
			<div class="flex-1 bg-gradient-to-br from-primary to-blue-800">
				<!-- Geometric Pattern Background -->
				<div class="absolute inset-0 opacity-10">
					<svg
						class="absolute inset-0 h-full w-full"
						xmlns="http://www.w3.org/2000/svg"
					>
						<defs>
							<pattern
								id="geometric-pattern"
								x="0"
								y="0"
								width="40"
								height="40"
								patternUnits="userSpaceOnUse"
							>
								<circle
									cx="20"
									cy="20"
									r="2"
									fill="currentColor"
								/>
								<path
									d="M20 0 L40 20 L20 40 L0 20 Z"
									fill="none"
									stroke="currentColor"
									stroke-width="1"
								/>
							</pattern>
						</defs>
						<rect
							width="100%"
							height="100%"
							fill="url(#geometric-pattern)"
						/>
					</svg>
				</div>

				<!-- Logo Area -->
				<div class="absolute top-8 left-8 z-10">
					<div class="flex items-center">
						<!-- Logo Placeholder - Replace with your actual logo -->
						<div
							class="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm"
						>
							<img
								src="/assets/Logo/nexus.svg"
								alt="Nexus Logo"
								class="w-8 h-8 object-contain"
								onerror="
									this.style.display = 'none';
									this.nextElementSibling.style.display =
										'block';
								"
							/>
							<!-- Fallback icon if logo fails to load -->
							<svg
								style="display: none"
								class="w-7 h-7 text-white"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
							</svg>
						</div>
						<span class="ml-3 text-2xl font-bold text-white"
							>ClientPortal</span
						>
					</div>
				</div>

				<!-- Content -->
				<div
					class="relative flex flex-col justify-center h-full px-12 text-white"
				>
					<div class="max-w-md mx-auto">
						<h3 class="text-4xl font-bold leading-tight mb-6">
							Almost There!
						</h3>
						<p class="text-xl leading-relaxed opacity-90 mb-8">
							Create a strong password to secure your account and
							complete your registration process.
						</p>

						<!-- Security Points -->
						<div class="space-y-4">
							<div class="flex items-center">
								<div
									class="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3"
								>
									<svg
										class="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
								<span class="text-lg opacity-90"
									>Password Protection</span
								>
							</div>
							<div class="flex items-center">
								<div
									class="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3"
								>
									<svg
										class="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
								<span class="text-lg opacity-90"
									>Account Security</span
								>
							</div>
							<div class="flex items-center">
								<div
									class="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3"
								>
									<svg
										class="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
								<span class="text-lg opacity-90"
									>Ready to Go</span
								>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Right Panel - Password Form Section -->
		<div
			class="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-12 xl:px-16 bg-white"
		>
			<div class="mx-auto w-full max-w-md">
				<!-- Logo for Mobile/Small Screens -->
				<div class="lg:hidden mb-8 text-center">
					<div class="flex justify-center items-center mb-4">
						<!-- Logo Placeholder for mobile -->
						<div
							class="w-12 h-12 bg-primary bg-opacity-10 rounded-xl flex items-center justify-center mr-3"
						>
							<img
								src="/path/to/your/logo.png"
								alt="Company Logo"
								class="w-8 h-8 object-contain"
								onerror="
									this.style.display = 'none';
									this.nextElementSibling.style.display =
										'block';
								"
							/>
							<svg
								style="display: none"
								class="w-7 h-7 text-primary"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
							</svg>
						</div>
						<span class="text-2xl font-bold text-gray-900"
							>InsurePortal</span
						>
					</div>
				</div>

				<!-- Header -->
				<div class="space-y-8">
					<div>
						<h2 class="text-3xl font-bold text-gray-900 mb-3">
							Set Your Password
						</h2>
						<p class="text-gray-600 text-base leading-relaxed">
							Create a password for your account associated with
							<strong class="text-gray-900">{{
								(selectAuthData && selectAuthData.email) ||
								'your email'
							}}</strong
							>.
						</p>
					</div>

					<!-- Password Form -->
					<form
						@submit.prevent="handlePasswordSubmit"
						class="space-y-8"
					>
						<!-- Password Fields Container -->
						<div class="space-y-6">
							<!-- Password Field -->
							<div class="relative">
								<label
									for="password"
									class="block text-sm font-medium text-gray-700 mb-2"
								>
									Password
								</label>
								<div class="relative">
									<input
										id="password"
										v-model="form.password"
										:type="
											showPassword ? 'text' : 'password'
										"
										required
										class="w-full px-4 py-3 text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 transition-all duration-200"
										placeholder="Enter your password"
										@input="validatePassword"
									/>
									<button
										type="button"
										@click="showPassword = !showPassword"
										class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
									>
										<svg
											v-if="showPassword"
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
											/>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
											/>
										</svg>
										<svg
											v-else
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
											/>
										</svg>
									</button>
								</div>
							</div>

							<!-- Confirm Password Field -->
							<div class="relative">
								<label
									for="confirmPassword"
									class="block text-sm font-medium text-gray-700 mb-2"
								>
									Confirm Password
								</label>
								<div class="relative">
									<input
										id="confirmPassword"
										v-model="form.confirmPassword"
										:type="
											showConfirmPassword
												? 'text'
												: 'password'
										"
										required
										class="w-full px-4 py-3 text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 transition-all duration-200"
										placeholder="Confirm your password"
										@input="validateConfirmPassword"
									/>
									<button
										type="button"
										@click="
											showConfirmPassword =
												!showConfirmPassword
										"
										class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
									>
										<svg
											v-if="showConfirmPassword"
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
											/>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
											/>
										</svg>
										<svg
											v-else
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
											/>
										</svg>
									</button>
								</div>
							</div>

							<!-- Password Strength Indicator -->
							<div v-if="form.password" class="space-y-2">
								<div class="text-xs font-medium text-gray-700">
									Password Strength
								</div>
								<div class="flex space-x-1">
									<div
										v-for="i in 4"
										:key="i"
										class="flex-1 h-2 rounded-full"
										:class="getStrengthBarClass(i)"
									></div>
								</div>
								<div
									class="text-xs"
									:class="getStrengthTextClass()"
								>
									{{ getStrengthText() }}
								</div>
							</div>
						</div>

						<!-- Error Message -->
						<div v-if="error" class="rounded-lg bg-red-50 p-4">
							<div class="text-sm text-red-700 text-center">
								{{ error }}
							</div>
						</div>

						<!-- Submit Button -->
						<button
							type="submit"
							:disabled="loading || !isFormValid"
							class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
						>
							<span v-if="loading" class="flex items-center">
								<svg
									class="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
								Processing...
							</span>
							<span v-else>Set Password & Complete</span>
						</button>
					</form>

					<!-- Back to Login -->
					<div class="text-center">
						<span class="text-gray-600 text-sm"
							>Already have an account?</span
						>
						<button
							@click="$router.push('/insurance/auth')"
							class="ml-1 text-primary hover:text-blue-700 font-medium text-sm transition-colors duration-200"
						>
							Sign In
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Mobile Header (Brand Banner Collapsed) -->
		<div
			class="lg:hidden fixed top-0 left-0 right-0 bg-primary text-white px-4 py-3 z-10"
		>
			<div class="flex items-center justify-between">
				<div class="flex items-center">
					<div
						class="w-8 h-8 bg-white bg-opacity-20 rounded-md flex items-center justify-center mr-2"
					>
						<img
							src="/path/to/your/logo.png"
							alt="Company Logo"
							class="w-5 h-5 object-contain"
							onerror="
								this.style.display = 'none';
								this.nextElementSibling.style.display = 'block';
							"
						/>
						<svg
							style="display: none"
							class="w-4 h-4"
							fill="currentColor"
							viewBox="0 0 20 20"
						>
							<path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
						</svg>
					</div>
					<span class="font-semibold">InsurePortal</span>
				</div>
				<span class="text-sm opacity-90">Almost There!</span>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { SetCredentials, UpdatePassword } from '../api/auth';

export default {
	name: 'SetPassword',
	computed: {
		...mapGetters('auth', [
			'selectRegId',
			'selectAuthData',
			'isPasswordReset',
			'forgotPasswordRef',
		]),
		isFormValid() {
			return (
				this.form.password.length >= 8 &&
				this.form.password === this.form.confirmPassword &&
				this.passwordStrength >= 2
			);
		},
	},
	data() {
		return {
			form: {
				password: '',
				confirmPassword: '',
			},
			showPassword: false,
			showConfirmPassword: false,
			error: '',
			loading: false,
			passwordStrength: 0,
		};
	},
	methods: {
		...mapActions('auth', ['setIsAuthenticated', 'setIsPasswordReset']),

		credentials() {
			return {
				registrationId: this.selectRegId,
				username: this.selectAuthData.email,
				password: this.form.password,
			};
		},

		validatePassword() {
			const password = this.form.password;
			let strength = 0;

			if (password.length >= 8) strength++;
			if (/[a-z]/.test(password)) strength++;
			if (/[A-Z]/.test(password)) strength++;
			if (/[0-9]/.test(password)) strength++;
			if (/[^A-Za-z0-9]/.test(password)) strength++;

			this.passwordStrength = strength;
			this.validateConfirmPassword();
		},

		validateConfirmPassword() {
			if (
				this.form.confirmPassword &&
				this.form.password !== this.form.confirmPassword
			) {
				this.error = 'Passwords do not match';
			} else {
				this.error = '';
			}
		},

		getStrengthBarClass(index) {
			if (index <= this.passwordStrength) {
				if (this.passwordStrength <= 2) return 'bg-red-400';
				if (this.passwordStrength <= 3) return 'bg-yellow-400';
				return 'bg-green-400';
			}
			return 'bg-gray-200';
		},

		getStrengthTextClass() {
			if (this.passwordStrength <= 2) return 'text-red-600';
			if (this.passwordStrength <= 3) return 'text-yellow-600';
			return 'text-green-600';
		},

		getStrengthText() {
			if (this.passwordStrength <= 1) return 'Weak';
			if (this.passwordStrength <= 2) return 'Fair';
			if (this.passwordStrength <= 3) return 'Good';
			return 'Strong';
		},

		async handlePasswordSubmit() {
			this.error = '';
			this.loading = true;

			try {
				if (this.form.password !== this.form.confirmPassword) {
					this.error = 'Passwords do not match';
					this.loading = false;
					return;
				}

				if (this.form.password.length < 8) {
					this.error = 'Password must be at least 8 characters long';
					this.loading = false;
					return;
				}
				if (!this.isPasswordReset) {
					const res = await SetCredentials(this.credentials());
					if (res.status) {
						this.$router.push('/insurance/auth');
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Account Created',
							message: `Please login to your account`,
							duration: 3000,
						});
					}
				} else {
					const res = await UpdatePassword({
						forgotPasswordSystemRef: this.forgotPasswordRef,
						newPassword: this.form.password,
					});
					if (res.status) {
						this.setIsPasswordReset(false);
						this.$router.push('/insurance/auth');
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Password reset successfull',
							message: `Please login to your account`,
							duration: 3000,
						});
					}
				}
			} catch (err) {
				let errorMessage = err.response.data.errors;
				this.error = errorMessage;
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Registration error',
					message: `${errorMessage}. Please try again`,
					duration: 3000,
				});
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>

<style scoped>
/* Mobile padding adjustment for fixed header */
@media (max-width: 1024px) {
	.flex-col {
		padding-top: 4rem;
	}
}

/* Enhanced focus effects for inputs */
input:focus {
	box-shadow: 0 0 0 2px rgba(17, 159, 225, 0.2);
	transform: scale(1.02);
}

/* Input styling */
input[type='password'],
input[type='text'] {
	transition: all 0.2s ease-in-out;
}

input[type='password']:hover,
input[type='text']:hover {
	border-color: #119fe1;
}

/* Button hover animations */
button {
	transition: all 0.2s ease-in-out;
}

button:hover:not(:disabled) {
	transform: translateY(-1px);
}

button:active:not(:disabled) {
	transform: translateY(0);
}

/* Logo fade-in animation */
.flex.items-center {
	animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Mobile optimizations */
@media (max-width: 640px) {
	input[type='password'],
	input[type='text'] {
		font-size: 1rem;
	}
}

/* Password strength bar animations */
.flex.space-x-1 > div {
	transition: background-color 0.3s ease-in-out;
}
</style>
